import { toast } from 'sonner'
import { useTabsStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { mutatorError } from '@basehub/data/replicache/mutators/repo-space/block/mutators/component'
import { invariant } from '@epic-web/invariant'
import { Replicache } from 'replicache'
import { RepoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import { UseMutationOptions, useMutation } from '@tanstack/react-query'
import { AsyncReturnType } from 'type-fest'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'

export const useMutateWithErrorHandling = <
  Variables,
  T extends (
    replicache: Replicache<RepoSpaceMutators>['mutate'],
    params: Variables
  ) => Promise<unknown>,
>(
  mutator: T,
  options?: { disableDefaultErrorHandling?: boolean } & UseMutationOptions<
    AsyncReturnType<T>,
    unknown,
    Variables
  >
) => {
  const { encodedSegment } = useBranchOrCommitSegment()
  const { data: selectedTeam } = useSelectedTeamAndRepo()
  const { team, repo } = selectedTeam || {}

  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const addTab = useTabsStore((state) => state.addTab)

  return useMutation(
    (vars: Variables) => {
      invariant(replicache, 'Replicache must be defined')
      return mutator(replicache.mutate, vars)
    },
    {
      // @ts-expect-error
      ...(options as unknown),
      onError: (error, variables, context) => {
        options?.onError?.(error, variables, context)
        if (options?.disableDefaultErrorHandling) return
        const parsedResult = mutatorError.safeParse(error)
        if (!parsedResult.success) {
          if (error instanceof MutatorUserError) {
            toast(error.reason)
          } else {
            toast('Unknown error occurred. Please try again.')
          }
          throw error
        }

        toast(
          <div className="flex w-full items-center justify-between space-x-2">
            <p>
              Can't delete component block{' '}
              <ButtonLink
                unstyled
                href={`/${team?.slug}/${repo?.slug}/explore/${encodedSegment}/${parsedResult.data.cause.block.id}`}
                onClick={() => {
                  addTab({
                    key: parsedResult.data.cause.block.id,
                    partiallyOpen: true,
                  })
                }}
                className="transition-colors duration-150 ease-in-out border-b border-dark-faint hover:border-dark-muted"
              >
                {parsedResult.data.cause.block.title || 'Untitled'}
              </ButtonLink>{' '}
              because it's being used by{' '}
              {parsedResult.data.cause.referencingBlock.type}{' '}
              <ButtonLink
                unstyled
                href={`/${team?.slug}/${repo?.slug}/explore/${encodedSegment}/${parsedResult.data.cause.referencingBlock.id}`}
                onClick={() => {
                  addTab({
                    key: parsedResult.data.cause.referencingBlock.id,
                    partiallyOpen: true,
                  })
                }}
                className="transition-colors duration-150 ease-in-out border-b border-dark-faint hover:border-dark-muted"
              >
                {parsedResult.data.cause.referencingBlock.title || 'Untitled'}
              </ButtonLink>
              .
            </p>
          </div>
        )

        throw error
      },
    }
  )
}
